import React, { useCallback, useState, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import ImageExtension from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import LinkExtension from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle, FontSize } from "@tiptap/extension-text-style";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, AlignLeft, AlignCenter, AlignRight,
  Link as LinkIcon, ImagePlus, Undo, Redo, RemoveFormatting,
  Upload, X, Loader2, Type,
} from "lucide-react";
import { uploadImage } from "../services/api";

const FONT_SIZES = [
  { label: "Small", value: "14px" },
  { label: "Normal", value: "16px" },
  { label: "Medium", value: "18px" },
  { label: "Large", value: "20px" },
  { label: "XL", value: "24px" },
  { label: "XXL", value: "30px" },
];

const MenuButton = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-1.5 rounded transition-all ${
      active
        ? "bg-[#1B7A3D] text-white"
        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
    } ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}
  >
    {children}
  </button>
);

const ToolbarDivider = () => <div className="w-px h-6 bg-gray-200 mx-1" />;

const ImageInsertDialog = ({ onClose, onInsert }) => {
  const [tab, setTab] = useState("upload");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [caption, setCaption] = useState("");
  const [credit, setCredit] = useState("");
  const [creditUrl, setCreditUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f) {
      setFile(f);
      setPreview(URL.createObjectURL(f));
      setError("");
    }
  };

  const handleInsert = async () => {
    setError("");
    let imageUrl = "";

    if (tab === "upload") {
      if (!file) { setError("Please select an image file"); return; }
      setUploading(true);
      try {
        imageUrl = await uploadImage(file);
      } catch {
        setError("Failed to upload image. Please try again.");
        setUploading(false);
        return;
      }
      setUploading(false);
    } else {
      if (!url.trim()) { setError("Please enter an image URL"); return; }
      imageUrl = url.trim();
    }

    onInsert({ src: imageUrl, caption, credit, creditUrl });
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900">Insert Image</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400" type="button">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Tabs */}
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => setTab("upload")}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${tab === "upload" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}
            >
              Upload File
            </button>
            <button
              type="button"
              onClick={() => setTab("url")}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${tab === "url" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}
            >
              Image URL
            </button>
          </div>

          {tab === "upload" ? (
            <div>
              {preview ? (
                <div className="relative rounded-lg overflow-hidden bg-gray-50">
                  <img src={preview} alt="Preview" className="w-full h-48 object-contain" />
                  <button
                    type="button"
                    onClick={() => { setFile(null); setPreview(""); if (fileRef.current) fileRef.current.value = ""; }}
                    className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => fileRef.current?.click()}
                  className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center cursor-pointer hover:border-[#1B7A3D] hover:bg-green-50/30 transition-all"
                >
                  <Upload className="mx-auto text-gray-400 mb-2" size={28} />
                  <p className="text-sm font-medium text-gray-600">Click to select image</p>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG, GIF, WEBP</p>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
            </div>
          ) : (
            <div>
              <input
                type="url"
                placeholder="https://example.com/image.jpg"
                value={url}
                onChange={(e) => { setUrl(e.target.value); setError(""); }}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7A3D]/20 focus:border-[#1B7A3D] transition-all"
              />
              {url && (
                <div className="mt-2 rounded-lg overflow-hidden bg-gray-50">
                  <img src={url} alt="Preview" className="w-full h-36 object-contain" onError={() => {}} />
                </div>
              )}
            </div>
          )}

          {/* Caption */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Caption (optional)</label>
            <input
              type="text"
              placeholder="Describe the image..."
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7A3D]/20 focus:border-[#1B7A3D] transition-all"
            />
          </div>

          {/* Photo Credit */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Photo Credit (optional)</label>
              <input
                type="text"
                placeholder="Photographer name"
                value={credit}
                onChange={(e) => setCredit(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7A3D]/20 focus:border-[#1B7A3D] transition-all"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Credit URL (optional)</label>
              <input
                type="url"
                placeholder="https://..."
                value={creditUrl}
                onChange={(e) => setCreditUrl(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7A3D]/20 focus:border-[#1B7A3D] transition-all"
              />
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleInsert}
            disabled={uploading}
            className="px-5 py-2 text-sm font-semibold text-white bg-[#1B7A3D] hover:bg-[#15612F] disabled:bg-gray-300 rounded-lg transition-colors flex items-center gap-2"
          >
            {uploading && <Loader2 className="animate-spin" size={14} />}
            Insert Image
          </button>
        </div>
      </div>
    </div>
  );
};

const RichTextEditor = ({ value, onChange }) => {
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showFontSize, setShowFontSize] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      TextStyle,
      FontSize,
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      LinkExtension.configure({ openOnClick: false }),
      ImageExtension.configure({ inline: false, allowBase64: true }),
      Placeholder.configure({ placeholder: "Write your article content here..." }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const handleImageInsert = useCallback(({ src, caption, credit, creditUrl }) => {
    if (!editor) return;

    // Insert image
    editor.chain().focus().setImage({ src, alt: caption || "", title: caption || "" }).run();

    // Build caption/credit line below the image
    const parts = [];
    if (caption) parts.push(caption);
    if (credit) {
      const creditText = creditUrl
        ? `<a href="${creditUrl}" target="_blank" rel="noopener">Photo: ${credit}</a>`
        : `Photo: ${credit}`;
      parts.push(creditText);
    }
    if (parts.length > 0) {
      editor.chain().focus().insertContent(`<p><em>${parts.join(" — ")}</em></p>`).run();
    }

    setShowImageDialog(false);
  }, [editor]);

  const addLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("Enter URL:", previousUrl || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
  }, [editor]);

  const currentFontSize = editor?.getAttributes("textStyle")?.fontSize || "";

  if (!editor) return null;

  return (
    <div className="tiptap-editor-wrapper border border-gray-200 rounded-xl overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-2 bg-gray-50 border-b border-gray-200">
        {/* Font Size Dropdown */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowFontSize(!showFontSize)}
            title="Font Size"
            className={`flex items-center gap-1 px-2 py-1.5 rounded text-sm transition-all ${
              currentFontSize ? "bg-[#1B7A3D]/10 text-[#1B7A3D]" : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <Type size={14} />
            <span className="text-xs font-medium hidden sm:inline">
              {currentFontSize ? FONT_SIZES.find(f => f.value === currentFontSize)?.label || "Size" : "Size"}
            </span>
          </button>
          {showFontSize && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowFontSize(false)} />
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-20 min-w-[120px]">
                <button
                  type="button"
                  onClick={() => { editor.chain().focus().unsetFontSize().run(); setShowFontSize(false); }}
                  className="w-full text-left px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  Default
                </button>
                {FONT_SIZES.map(({ label, value: size }) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => { editor.chain().focus().setFontSize(size).run(); setShowFontSize(false); }}
                    className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                      currentFontSize === size ? "bg-[#1B7A3D]/10 text-[#1B7A3D] font-medium" : "text-gray-700 hover:bg-gray-50"
                    }`}
                    style={{ fontSize: size }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <ToolbarDivider />

        <MenuButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold">
          <Bold size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic">
          <Italic size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline">
          <UnderlineIcon size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough">
          <Strikethrough size={16} />
        </MenuButton>

        <ToolbarDivider />

        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="Heading 1">
          <Heading1 size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading 2">
          <Heading2 size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Heading 3">
          <Heading3 size={16} />
        </MenuButton>

        <ToolbarDivider />

        <MenuButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet List">
          <List size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered List">
          <ListOrdered size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote">
          <Quote size={16} />
        </MenuButton>

        <ToolbarDivider />

        <MenuButton onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Align Left">
          <AlignLeft size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Align Center">
          <AlignCenter size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Align Right">
          <AlignRight size={16} />
        </MenuButton>

        <ToolbarDivider />

        <MenuButton onClick={addLink} active={editor.isActive("link")} title="Insert Link">
          <LinkIcon size={16} />
        </MenuButton>
        <MenuButton onClick={() => setShowImageDialog(true)} title="Insert Image">
          <ImagePlus size={16} />
        </MenuButton>

        <ToolbarDivider />

        <MenuButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
          <Undo size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
          <Redo size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} title="Clear Formatting">
          <RemoveFormatting size={16} />
        </MenuButton>
      </div>

      {/* Editor */}
      <EditorContent editor={editor} className="tiptap-content" />

      {/* Image Insert Dialog */}
      {showImageDialog && (
        <ImageInsertDialog
          onClose={() => setShowImageDialog(false)}
          onInsert={handleImageInsert}
        />
      )}
    </div>
  );
};

export default RichTextEditor;
